import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'

/** The three spans the dashboard's toggle offers (D-138). */
export type DashboardPeriod = 'today' | '7days' | '30days'

export const periodLabels: Record<DashboardPeriod, string> = {
  today: 'Today',
  '7days': '7 Days',
  '30days': '30 Days',
}

/** What each period is measured against, for the arrow beneath a figure. */
export const previousLabels: Record<DashboardPeriod, string> = {
  today: 'vs yesterday',
  '7days': 'vs previous 7 days',
  '30days': 'vs previous 30 days',
}

export type DashboardLocation = { id: string; code: string; name: string }

export type DashboardDay = { date: string; total: number; invoices: number }

/**
 * Stock that moved one way. `value` is at cost, and absent for someone who may not see
 * cost — the API leaves a null out of the JSON altogether, so every one of these is
 * optional rather than nullable and has to be tested with `== null`, never `=== null`.
 */
export type DashboardMovement = { units: number; value?: number; previousUnits: number }

export type DashboardShop = {
  locationId: string
  name: string
  sales: number
  invoices: number
  units: number
  target?: number
  collections: number
  expenses: number
}

/** The landing page's figures (D-136, D-138). A block is absent when the person may not see what it is made of. */
export type DashboardSummary = {
  today: string
  period: DashboardPeriod
  from: string
  to: string
  scope: string
  locationId?: string
  locations: DashboardLocation[]
  sales?: { total: number; invoices: number; previous: number; target?: number; series: DashboardDay[] }
  stock?: {
    sold: DashboardMovement
    transferredOut: DashboardMovement
    transferredIn: DashboardMovement
    adjusted: DashboardMovement
    lowItems: number
    outOfStock: number
    levelsSet: number
  }
  cash?: {
    /** Absent for someone who may see a branch's spending but not its receipts. */
    collected?: number
    collectedPrevious?: number
    pettyCash: number
    pettyCashCount: number
    netPosition?: number
    collectionTarget?: number
  }
  invoices?: { dueAmount: number; dueCount: number; paidAmount: number; paidCount: number }
  debtors?: { outstanding: number; overdue: number; customers: number; overdueCustomers: number }
  deliveries?: { active: number; outForDelivery: number; overdue: number }
  shops: DashboardShop[]
  activity: { kind: 'Sale' | 'Delivery' | 'Transfer'; code: string; title: string; detail?: string; amount?: number; at: string; link?: string }[]
}

export function useDashboard(period: DashboardPeriod, locationId?: string) {
  const query = new URLSearchParams({ period })
  if (locationId) query.set('locationId', locationId)

  // Fresh whenever it is opened — someone comes back to it straight from a sale — and
  // at most a minute old while it is left open at the counter.
  return useQuery({
    queryKey: ['dashboard', period, locationId ?? 'all'],
    queryFn: ({ signal }) => api<DashboardSummary>(`/api/dashboard?${query}`, { signal }),
    refetchInterval: 60_000,
    staleTime: 0,
  })
}
